/**
 * FrostedWaterDispenserV2Modal — Stitch v2 Dark Glass 飲水機控制面板
 *
 * Deep dark background, animated fluid ring, bento grid info cards
 * Dynamic 1~3 outlet temperatures, Water Levels, Filter, Child Lock
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import './FrostedWaterDispenserV2Modal.css'

export const WATER_DISPENSER_V2_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'ring', label: 'Temperature Ring', icon: 'thermostat', slots: [] },
  { id: 'mode', label: 'Mode Selector', icon: 'water_drop', slots: [] },
  { id: 'bento', label: 'Info Cards', icon: 'grid_view', slots: [] },
  { id: 'childLock', label: 'Child Lock', icon: 'lock', slots: [] },
  { id: 'filters', label: 'Filter Status', icon: 'filter_alt', slots: [] },
  { id: 'waterChart', label: 'Water Usage Chart', icon: 'bar_chart', slots: [] },
]

const DEFAULT_SECTION_ORDER = WATER_DISPENSER_V2_FROSTED_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------
function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) { if (key in state && state[key] !== undefined && state[key] !== null) return state[key] }
  return undefined
}
function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v); return isNaN(n) ? null : n
}
function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys); return v != null ? String(v) : ''
}

// ---------------------------------------------------------------------------
// Outlet definitions
// ---------------------------------------------------------------------------
type TempOutlet = {
  id: string; label: string; icon: string; colorClass: string
  tempAttrKeys: string[]; targetAttrKeys: string[]; levelAttrKeys: string[]
  setCmd: string; min: number; max: number; step: number; pickerTitle: string
}

const OUTLET_DEFS: TempOutlet[] = [
  {
    id: 'ice', label: 'Cold', icon: 'ac_unit', colorClass: 'ice',
    tempAttrKeys: ['iceTemp', 'icewaterTemperature', 'coldTemp'],
    targetAttrKeys: ['iceTargetTemp', 'icewaterTargetTemperature', 'coldTargetTemp'],
    levelAttrKeys: ['iceTankLevel', 'icewaterLevel', 'coldTankLevel'],
    setCmd: 'setIceTemp', min: 3, max: 10, step: 1, pickerTitle: 'Cold Target Temp',
  },
  {
    id: 'warm', label: 'Warm', icon: 'water_drop', colorClass: 'warm',
    tempAttrKeys: ['warmTemp', 'warmwaterTemperature'],
    targetAttrKeys: ['warmTargetTemp', 'warmwaterTargetTemperature'],
    levelAttrKeys: ['warmTankLevel', 'warmwaterLevel'],
    setCmd: 'setWarmTemp', min: 35, max: 55, step: 1, pickerTitle: 'Warm Target Temp',
  },
  {
    id: 'hot', label: 'Hot', icon: 'local_fire_department', colorClass: 'hot',
    tempAttrKeys: ['hotTemp', 'hotwaterTemperature'],
    targetAttrKeys: ['hotTargetTemp', 'hotwaterTargetTemperature'],
    levelAttrKeys: ['hotTankLevel', 'hotwaterLevel'],
    setCmd: 'setHotTemp', min: 80, max: 100, step: 1, pickerTitle: 'Hot Target Temp',
  },
]

const SINGLE_OUTLET: TempOutlet = {
  id: 'water', label: 'Water Temp', icon: 'water_drop', colorClass: 'warm',
  tempAttrKeys: ['waterTemp', 'waterTemperature', 'temperature'],
  targetAttrKeys: ['waterTargetTemp', 'targetTemperature'],
  levelAttrKeys: ['waterLevel', 'tankLevel'],
  setCmd: 'setTemperature', min: 5, max: 95, step: 1, pickerTitle: 'Target Temp',
}

// ---------------------------------------------------------------------------
// Filter definitions
// ---------------------------------------------------------------------------
type FilterDef = { id: string; name: string; attrKeys: string[] }
const FILTER_DEFS: FilterDef[] = [
  { id: 'pp', name: 'PP Filter', attrKeys: ['filterLifePP', 'ppFilterLife'] },
  { id: 'carbon', name: 'Carbon Filter', attrKeys: ['filterLifeCarbon', 'carbonFilterLife'] },
  { id: 'ro', name: 'RO Membrane', attrKeys: ['filterLifeRO', 'roFilterLife'] },
  { id: 'generic', name: 'Filter', attrKeys: ['filterLife'] },
]

function filterStatus(pct: number): { cls: string; fillCls: string } {
  if (pct >= 50) return { cls: 'wdisp2-frosted__filter-pct--good', fillCls: 'wdisp2-frosted__filter-fill--good' }
  if (pct >= 30) return { cls: 'wdisp2-frosted__filter-pct--warn', fillCls: 'wdisp2-frosted__filter-fill--warn' }
  return { cls: 'wdisp2-frosted__filter-pct--low', fillCls: 'wdisp2-frosted__filter-fill--low' }
}

function estimateDaysRemaining(pct: number): string {
  const days = Math.round(pct * 1.5)
  return `~${days} days remaining`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function FrostedWaterDispenserV2Modal({
  device, deviceState, onCommand, onClose, isEditorMode, cssOverrides,
  sectionOrder, sectionVisible, onSectionContextMenu, highlightedSection, credentials,
}: DeviceModalTemplateProps) {

  const handleContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!isEditorMode || !onSectionContextMenu) return
    const sectionEl = (event.target as HTMLElement)?.closest('[data-modal-section]') as HTMLElement | null
    if (!sectionEl) return
    event.preventDefault(); event.stopPropagation()
    const sectionId = sectionEl.dataset.modalSection || ''
    if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
  }, [isEditorMode, onSectionContextMenu])

  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    return [...order, ...DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))]
  }, [sectionOrder])
  const isSectionVisible = useCallback((id: string) => sectionVisible?.[id] !== false, [sectionVisible])

  // ── Mock data (when no real device data or no water dispenser keys) ──
  const MOCK_STATE: Record<string, unknown> = {
    on: true,
    iceTemp: 4, iceTargetTemp: 5, iceTankLevel: 84,
    warmTemp: 45, warmTargetTemp: 48, warmTankLevel: 62,
    hotTemp: 92, hotTargetTemp: 95, hotTankLevel: 45,
    filterLife: 92, tds: 12,
    childLock: false,
  }
  const allTempKeys = [...OUTLET_DEFS.flatMap(d => d.tempAttrKeys), ...SINGLE_OUTLET.tempAttrKeys]
  const hasAnyTempKey = deviceState && allTempKeys.some(k => k in deviceState && deviceState[k] != null)
  const isMock = !deviceState || Object.keys(deviceState).length === 0 || !hasAnyTempKey
  const effectiveState = isMock ? { ...MOCK_STATE, ...(deviceState || {}) } : deviceState

  // ── Device state ──
  const isOn = Boolean(getAttr(effectiveState, 'on'))

  const activeOutlets = useMemo(() => {
    const found: TempOutlet[] = []
    for (const def of OUTLET_DEFS) {
      const hasTemp = def.tempAttrKeys.some(k => effectiveState && k in effectiveState && effectiveState[k] != null)
      if (hasTemp) found.push(def)
    }
    if (found.length === 0) {
      const hasSingle = SINGLE_OUTLET.tempAttrKeys.some(k => effectiveState && k in effectiveState && effectiveState[k] != null)
      if (hasSingle) return [SINGLE_OUTLET]
      return OUTLET_DEFS
    }
    return found
  }, [effectiveState])

  const activeFilters = useMemo(() => {
    const found: Array<FilterDef & { pct: number }> = []
    for (const def of FILTER_DEFS) {
      const val = numAttr(effectiveState, ...def.attrKeys)
      if (val !== null) found.push({ ...def, pct: val })
    }
    return found
  }, [effectiveState])

  const childLock = Boolean(getAttr(effectiveState, 'childLock', 'lockState', 'isChildLocked'))

  // Selected outlet for central ring (user can switch via mode buttons)
  const [selectedOutletId, setSelectedOutletId] = useState<string>(activeOutlets[0]?.id || 'ice')
  const selectedOutlet = activeOutlets.find(o => o.id === selectedOutletId) || activeOutlets[0]
  const selectedTemp = numAttr(effectiveState, ...selectedOutlet.tempAttrKeys)
  const selectedTarget = numAttr(effectiveState, ...selectedOutlet.targetAttrKeys)

  // Status label for ring
  const ringStatusLabel = selectedOutlet.id === 'ice' ? 'Cooling' : selectedOutlet.id === 'hot' ? 'Heating' : 'Warming'

  // ── Settings ──
  const [showSettings, setShowSettings] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {}); setCopiedField(label); setTimeout(() => setCopiedField(null), 1000)
  }, [])

  // ── Rename ──
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])
  const startRename = useCallback(() => { setRenameValue(displayName); setIsRenaming(true); setTimeout(() => renameInputRef.current?.focus(), 50) }, [displayName])
  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) { setIsRenaming(false); return }
    setDisplayName(trimmed); setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  // ── Pending state ──
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const [pendingLock, setPendingLock] = useState<boolean | null>(null)
  const displayPower = pendingPower ?? isOn
  const displayLock = pendingLock ?? childLock
  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])
  useEffect(() => { if (pendingLock !== null && pendingLock === childLock) setPendingLock(null) }, [childLock, pendingLock])

  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  const togglePower = useCallback(() => {
    if (isEditorMode) return; const next = !displayPower; setPendingPower(next); execCmd(next ? 'on' : 'off')
  }, [isEditorMode, displayPower, execCmd])

  const toggleChildLock = useCallback(() => {
    if (isEditorMode) return; const next = !displayLock; setPendingLock(next); execCmd('setChildLock', { lock: next })
  }, [isEditorMode, displayLock, execCmd])

  // ── Picker ──
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerOutlet, setPickerOutlet] = useState<TempOutlet | null>(null)
  const [pickerValue, setPickerValue] = useState(0)
  const pickerWheelRef = useRef<HTMLDivElement>(null)

  const openPicker = useCallback((outlet: TempOutlet) => {
    if (!displayPower) return
    const currentTarget = numAttr(effectiveState, ...outlet.targetAttrKeys) ?? numAttr(effectiveState, ...outlet.tempAttrKeys) ?? outlet.min
    setPickerOutlet(outlet); setPickerValue(currentTarget); setPickerOpen(true)
  }, [isEditorMode, displayPower, effectiveState])

  const confirmPicker = useCallback(() => {
    if (pickerOutlet) execCmd(pickerOutlet.setCmd, { temperature: pickerValue })
    setPickerOpen(false); setPickerOutlet(null)
  }, [pickerOutlet, pickerValue, execCmd])

  const closePicker = useCallback(() => { setPickerOpen(false); setPickerOutlet(null) }, [])

  const pickerItems = useMemo(() => {
    if (!pickerOutlet) return []
    const items: number[] = []
    for (let t = pickerOutlet.min; t <= pickerOutlet.max; t += pickerOutlet.step) items.push(t)
    return items
  }, [pickerOutlet])

  useEffect(() => {
    if (!pickerOpen || !pickerWheelRef.current || !pickerOutlet) return
    const idx = pickerItems.indexOf(pickerValue)
    if (idx >= 0) setTimeout(() => { pickerWheelRef.current?.scrollTo({ top: idx * 40 }) }, 60)
  }, [pickerOpen, pickerOutlet, pickerValue, pickerItems])

  // ── Water usage chart ──
  const [chartRange, setChartRange] = useState<'daily' | 'weekly'>('daily')

  const chartData = useMemo(() => {
    // Mock data — TODO: replace with real API data when available
    const days = chartRange === 'daily' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] : ['W1', 'W2', 'W3', 'W4']
    return days.map(label => ({
      label,
      cold: Math.round(200 + Math.random() * 400),
      warm: Math.round(100 + Math.random() * 300),
      hot: Math.round(50 + Math.random() * 200),
    }))
  }, [chartRange])

  const chartMax = useMemo(() => {
    let max = 0
    for (const d of chartData) max = Math.max(max, d.cold, d.warm, d.hot)
    return max || 1
  }, [chartData])

  const chartTotal = useMemo(() => {
    let cold = 0, warm = 0, hot = 0
    for (const d of chartData) { cold += d.cold; warm += d.warm; hot += d.hot }
    return { cold, warm, hot, total: cold + warm + hot }
  }, [chartData])

  const stateClass = displayPower ? 'wdisp2-frosted--on' : 'wdisp2-frosted--off'
  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  // ── Section renderers ──
  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null
    switch (sectionId) {
      case 'ring':
        return (
          <div key="ring" className={`wdisp2-frosted__ring-section${hl('ring')}`} data-modal-section="ring"
            data-outlet={selectedOutletId}>
            <div className="wdisp2-frosted__ring-container">
              <div className="wdisp2-frosted__ring-glow" />
              <div className="wdisp2-frosted__ring-pulse" />
              <div className="wdisp2-frosted__ring-inner" />
              <div className="wdisp2-frosted__ring-fluid" />
              <div className="wdisp2-frosted__ring-content">
                <span className="wdisp2-frosted__ring-status">
                  {displayPower ? ringStatusLabel : 'Standby'}
                </span>
                <div className="wdisp2-frosted__ring-temp">
                  {displayPower ? (selectedTemp ?? '--') : '--'}
                  <span className="wdisp2-frosted__ring-temp-unit">{'\u00B0C'}</span>
                </div>
                {displayPower && selectedOutlet.id !== 'warm' ? (
                  <span className="wdisp2-frosted__ring-target" onClick={() => openPicker(selectedOutlet)}>
                    {`Target ${selectedTarget ?? '--'}\u00B0C`}
                  </span>
                ) : displayPower ? (
                  <span className="wdisp2-frosted__ring-target" style={{ cursor: 'default', background: 'transparent', border: 'none' }}>
                    Room Temp
                  </span>
                ) : (
                  <span className="wdisp2-frosted__ring-target" style={{ cursor: 'default', background: 'transparent', border: 'none' }}>
                    OFF
                  </span>
                )}
              </div>
            </div>
          </div>
        )

      case 'mode':
        return (
          <div key="mode" className={`wdisp2-frosted__modes${hl('mode')}`} data-modal-section="mode">
            <div className="wdisp2-frosted__modes-pill">
              {activeOutlets.map(outlet => (
                <button key={outlet.id}
                  className={`wdisp2-frosted__mode-btn${selectedOutletId === outlet.id ? ' active' : ''}`}
                  data-mode={outlet.id}
                  onClick={() => setSelectedOutletId(outlet.id)}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{outlet.icon}</span>
                  {outlet.label}
                </button>
              ))}
            </div>
          </div>
        )

      case 'bento': {
        // Water level rows
        const levelRows: Array<{ label: string; colorSuffix: string; pct: number }> = []
        for (const outlet of activeOutlets) {
          const level = numAttr(effectiveState, ...outlet.levelAttrKeys)
          if (level != null) {
            levelRows.push({
              label: outlet.id === 'ice' ? 'COLD' : outlet.id === 'warm' ? 'WARM' : outlet.id === 'hot' ? 'HOT' : 'WATER',
              colorSuffix: outlet.colorClass,
              pct: Math.max(0, Math.min(100, level)),
            })
          }
        }
        if (levelRows.length === 0) {
          const wl = numAttr(effectiveState, 'waterLevel', 'tankLevel')
          if (wl != null) levelRows.push({ label: 'WATER', colorSuffix: 'ice', pct: Math.max(0, Math.min(100, wl)) })
        }

        // Filter for bento card
        const bestFilter = activeFilters.length > 0 ? activeFilters[0] : null

        // TDS value
        const tds = numAttr(effectiveState, 'tds', 'tdsValue', 'waterPurity')

        return (
          <div key="bento" className={`wdisp2-frosted__bento${hl('bento')}`} data-modal-section="bento">
            {/* Water Levels Card */}
            <div className="wdisp2-frosted__bento-card wdisp2-glass">
              <div className="wdisp2-frosted__bento-top">
                <span className="wdisp2-frosted__bento-icon material-symbols-outlined">humidity_mid</span>
                <span className="wdisp2-frosted__bento-label">Water Levels</span>
              </div>
              <div className="wdisp2-frosted__wl-rows">
                {levelRows.length > 0 ? levelRows.map(row => (
                  <div key={row.label} className="wdisp2-frosted__wl-row">
                    <div className="wdisp2-frosted__wl-meta">
                      <span>{row.label}</span>
                      <span className={`wdisp2-frosted__wl-meta-pct${row.colorSuffix !== 'ice' ? ` wdisp2-frosted__wl-meta-pct--${row.colorSuffix}` : ''}`}>{row.pct}%</span>
                    </div>
                    <div className="wdisp2-frosted__wl-bar">
                      <div className={`wdisp2-frosted__wl-fill${row.colorSuffix !== 'ice' ? ` wdisp2-frosted__wl-fill--${row.colorSuffix}` : ''}`} style={{ width: `${row.pct}%` }} />
                    </div>
                  </div>
                )) : (
                  <div className="wdisp2-frosted__bento-sub">No data</div>
                )}
              </div>
            </div>

            {/* Filter Card */}
            <div className="wdisp2-frosted__bento-card wdisp2-glass">
              <div className="wdisp2-frosted__bento-top">
                <span className="wdisp2-frosted__bento-icon wdisp2-frosted__bento-icon--secondary material-symbols-outlined">filter_alt</span>
                <span className="wdisp2-frosted__bento-label">Filter</span>
              </div>
              <div>
                <div className="wdisp2-frosted__bento-value">{bestFilter ? `${bestFilter.pct}%` : '--'}</div>
                <div className="wdisp2-frosted__bento-sub">
                  {bestFilter ? `Replace in ~${Math.round(bestFilter.pct * 1.5)} days` : 'No data'}
                </div>
              </div>
            </div>

            {/* TDS Wide Card */}
            {tds != null && (
              <div className="wdisp2-frosted__bento-card wdisp2-frosted__bento-card--wide wdisp2-glass">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(210,119,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="wdisp2-frosted__bento-icon--tertiary material-symbols-outlined">biotech</span>
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>Water Purity (TDS)</div>
                    <div className="wdisp2-frosted__bento-sub">Excellent drinking quality</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: 28, fontWeight: 800 }}>{tds}</span>
                  <span className="wdisp2-frosted__bento-sub" style={{ marginLeft: 4 }}>PPM</span>
                </div>
              </div>
            )}
          </div>
        )
      }

      case 'childLock':
        return (
          <div key="childLock" className={`wdisp2-frosted__lock-row${hl('childLock')}`} data-modal-section="childLock">
            <div className="wdisp2-frosted__lock-left">
              <span className="wdisp2-frosted__lock-icon material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>enhanced_encryption</span>
              <span className="wdisp2-frosted__lock-label">Safety Child Lock</span>
            </div>
            <button className={`wdisp2-frosted__toggle${displayLock ? ' wdisp2-frosted__toggle--active' : ''}`} onClick={toggleChildLock}>
              <div className="wdisp2-frosted__toggle-knob" />
            </button>
          </div>
        )

      case 'filters': {
        if (activeFilters.length === 0) return null
        return (
          <div key="filters" className={`wdisp2-frosted__filters${hl('filters')}`} data-modal-section="filters">
            {activeFilters.map(f => {
              const st = filterStatus(f.pct)
              const isLow = f.pct < 30
              return (
                <div key={f.id} className="wdisp2-frosted__filter-card wdisp2-glass">
                  <div className="wdisp2-frosted__filter-header">
                    <div className="wdisp2-frosted__filter-name">
                      <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>filter_alt</span>
                      {f.name}
                    </div>
                    <div className={`wdisp2-frosted__filter-pct ${st.cls}`}>{f.pct}%</div>
                  </div>
                  <div className="wdisp2-frosted__filter-bar">
                    <div className={`wdisp2-frosted__filter-fill ${st.fillCls}`} style={{ width: `${f.pct}%` }} />
                  </div>
                  <div className="wdisp2-frosted__filter-sub">
                    <span>{estimateDaysRemaining(f.pct)}{isLow ? ' \u26A0\uFE0F' : ''}</span>
                    <button className={`wdisp2-frosted__filter-buy${isLow ? ' wdisp2-frosted__filter-buy--urgent' : ''}`}>
                      <span className="material-symbols-outlined" style={{ fontSize: 12, fontVariationSettings: "'FILL' 1" }}>shopping_cart</span>
                      {isLow ? 'Buy Now' : 'Buy'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )
      }

      case 'waterChart':
        return (
          <div key="waterChart" className={`wdisp2-frosted__chart${hl('waterChart')}`} data-modal-section="waterChart">
            <div className="wdisp2-frosted__chart-header">
              <span className="wdisp2-frosted__chart-title">
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>bar_chart</span>
                Water Usage
              </span>
              <div className="wdisp2-frosted__chart-range">
                <button className={`wdisp2-frosted__chart-range-btn${chartRange === 'daily' ? ' active' : ''}`}
                  onClick={() => setChartRange('daily')}>Daily</button>
                <button className={`wdisp2-frosted__chart-range-btn${chartRange === 'weekly' ? ' active' : ''}`}
                  onClick={() => setChartRange('weekly')}>Weekly</button>
              </div>
            </div>
            <div className="wdisp2-frosted__chart-bars">
              {chartData.map(d => (
                <div key={d.label} className="wdisp2-frosted__chart-group">
                  <div className="wdisp2-frosted__chart-bar-set">
                    <div className="wdisp2-frosted__chart-bar wdisp2-frosted__chart-bar--cold"
                      style={{ height: `${(d.cold / chartMax) * 100}%` }}
                      title={`${d.cold}ml`} />
                    <div className="wdisp2-frosted__chart-bar wdisp2-frosted__chart-bar--warm"
                      style={{ height: `${(d.warm / chartMax) * 100}%` }}
                      title={`${d.warm}ml`} />
                    <div className="wdisp2-frosted__chart-bar wdisp2-frosted__chart-bar--hot"
                      style={{ height: `${(d.hot / chartMax) * 100}%` }}
                      title={`${d.hot}ml`} />
                  </div>
                  <span className="wdisp2-frosted__chart-day">{d.label}</span>
                </div>
              ))}
            </div>
            <div className="wdisp2-frosted__chart-legend">
              <span className="wdisp2-frosted__chart-legend-item">
                <span className="wdisp2-frosted__chart-legend-dot wdisp2-frosted__chart-legend-dot--cold" />
                Cold {chartTotal.cold}ml
              </span>
              <span className="wdisp2-frosted__chart-legend-item">
                <span className="wdisp2-frosted__chart-legend-dot wdisp2-frosted__chart-legend-dot--warm" />
                Warm {chartTotal.warm}ml
              </span>
              <span className="wdisp2-frosted__chart-legend-item">
                <span className="wdisp2-frosted__chart-legend-dot wdisp2-frosted__chart-legend-dot--hot" />
                Hot {chartTotal.hot}ml
              </span>
            </div>
          </div>
        )

      default: return null
    }
  }

  return (
    <div className={`wdisp2-frosted ${stateClass}${pickerOpen || showSettings ? ' wdisp2-frosted--no-scroll' : ''}`} data-modal-template-id="water-dispenser-frosted-v2" onContextMenu={handleContextMenu}>
      {cssOverrides && <style>{cssOverrides}</style>}
      {/* Background auras */}
      <div className="wdisp2-frosted__aura-1" />
      <div className="wdisp2-frosted__aura-2" />

      <div className="wdisp2-frosted__header" data-modal-section="_modal">
        <button className="wdisp2-frosted__back" onClick={onClose} aria-label="Back">{'\u2039'}</button>
        <span className="wdisp2-frosted__name">{displayName}</span>
        <button className={`wdisp2-frosted__power-btn${displayPower ? ' wdisp2-frosted__power-btn--on' : ''}`} onClick={togglePower} aria-label="Power">
          <span className="material-symbols-outlined" style={{ fontSize: 20, fontVariationSettings: "'FILL' 1" }}>power_settings_new</span>
        </button>
      </div>

      {orderedSections.map(id => renderSection(id))}

      <button className="wdisp2-frosted__settings-btn" onClick={() => setShowSettings(true)} aria-label="Settings">
        <span className="material-symbols-outlined" style={{ fontSize: 24 }}>settings</span>
      </button>

      {/* Settings overlay */}
      {showSettings && (
        <div className="wdisp2-frosted__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="wdisp2-frosted__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="wdisp2-frosted__settings-header">
              <span className="wdisp2-frosted__settings-title">Settings</span>
              <button className="wdisp2-frosted__settings-close" onClick={() => setShowSettings(false)}>{'\u2715'}</button>
            </div>
            <div className="wdisp2-frosted__settings-section">
              <div className="wdisp2-frosted__settings-section-title">Device Name</div>
              {isRenaming ? (
                <div className="wdisp2-frosted__settings-row">
                  <input ref={renameInputRef} className="wdisp2-frosted__rename-input" value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={submitRename} maxLength={40} />
                </div>
              ) : (
                <div className="wdisp2-frosted__settings-row">
                  <span className="wdisp2-frosted__settings-value" style={{ textAlign: 'left', flex: 1 }}>{displayName}</span>
                  <button className="wdisp2-frosted__settings-edit" onClick={startRename} title="Rename">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                  </button>
                </div>
              )}
            </div>
            <div className="wdisp2-frosted__settings-section">
              <div className="wdisp2-frosted__settings-section-title">Device Info</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="wdisp2-frosted__settings-row">
                  <span className="wdisp2-frosted__settings-label">{item.label}</span>
                  <span className="wdisp2-frosted__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button className="wdisp2-frosted__settings-copy" onClick={() => copyToClipboard(item.value, item.label)} title="Copy">
                      {'\uD83D\uDCCB'}{copiedField === item.label && <span className="wdisp2-frosted__copied-toast">Copied</span>}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Temperature Picker */}
      {pickerOpen && pickerOutlet && (
        <div className="wdisp2-frosted__picker-overlay" onClick={closePicker}>
          <div className="wdisp2-frosted__picker" onClick={e => e.stopPropagation()}>
            <div className="wdisp2-frosted__picker-header">
              <div className="wdisp2-frosted__picker-title">{pickerOutlet.pickerTitle}</div>
              <button className="wdisp2-frosted__picker-done" onClick={confirmPicker}>Done</button>
            </div>
            <div className="wdisp2-frosted__picker-wheel" ref={pickerWheelRef}>
              <div style={{ height: 80 }} />
              {pickerItems.map(t => (
                <div key={t}
                  className={`wdisp2-frosted__picker-item${t === pickerValue ? ' wdisp2-frosted__picker-item--selected' : ''}`}
                  onClick={() => setPickerValue(t)}>
                  {t}{'\u00B0C'}
                </div>
              ))}
              <div style={{ height: 80 }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
